import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";

import ContentPlatformImage from "@/components/content-platform-image/src";
import { PullQuote } from "@/libraries/design/components";

type Nullable<T> = T | null;

export interface CustomerStoriesDetailsHeroQuoteProps {
    imgSrc?: Nullable<string>;
    imgAlt?: Nullable<string>;
    quote?: Nullable<string>;
    author?: Nullable<string>;
    role?: Nullable<string>;
    company?: Nullable<string>;
}

const quoteBodyCss = css({
    gridColumnStart: 1,
    gridColumnEnd: -1,
    // Remove default padding on Pull Quote
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    div: {
        paddingBlock: "0 !important"
    }
});

const quoteStylingWithImage = css({
    [media.above.md]: {
        gridColumn: "5 / -1"
    }
});

const quoteStylingNoImage = css({
    [media.above.md]: {
        gridColumn: "3 / span 8"
    }
});

const imageCss = css({
    gridColumnStart: 1,
    gridColumnEnd: 7,
    [media.below.xs]: {
        gridColumnStart: 1,
        gridColumnEnd: -1
    },
    [media.above.md]: {
        gridColumnStart: 1,
        gridColumnEnd: 5
    }
});

const quoteContainerStyles = css({
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: "24px",
    alignItems: "center",
    justifyItems: "start",
    [media.above.md]: {
        gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
        gap: "32px"
    }
});

export const CustomerStoriesDetailsHeroQuote = ({
    imgSrc,
    imgAlt,
    quote,
    author,
    role,
    company
}: CustomerStoriesDetailsHeroQuoteProps) => {
    const authorDetails: string = [role, company].filter(Boolean).join(", ");

    return (
        <div css={quoteContainerStyles}>
            {imgSrc && (
                <div css={imageCss}>
                    <ContentPlatformImage
                        contentType={
                            /* Since we do not pass in the direct Contentful image via Uniform,
                            this uses a rough assumption to trigger a webp transformation */
                            imgSrc.endsWith(".png") ? "image/png" : undefined
                        }
                        src={imgSrc}
                        alt={imgAlt ?? ""}
                        style={{ maxWidth: "100%" }}
                        baseWidth={440}
                    />
                </div>
            )}
            <div
                css={[
                    quoteBodyCss,
                    imgSrc ? quoteStylingWithImage : quoteStylingNoImage
                ]}
            >
                <PullQuote
                    quote={quote ?? ""}
                    authorName={author ?? ""}
                    authorDetails={authorDetails}
                    alignment={imgSrc ? "start" : "center"}
                    size="large"
                    variant="basic"
                />
            </div>
        </div>
    );
};
